import Chart from 'chart.js/auto';
import CompareChartBase from './compare_chart_base';
import { t } from '../javascript/lib/i18n';

// Clean tick intervals for the response time axis (in seconds).
// Chart.js picks the smallest one that yields roughly 5-8 ticks.
const TICK_STEPS = [
  60, 120, 300, 600, 900, 1800, 3600, 7200, 14400, 21600, 43200, 86400,
];

export default class extends CompareChartBase {
  connect() {
    this.visibleLayers = new Set(['line']);
    // The layer toggles render in the page shell, outside this chart's turbo
    // frame, and drive visibility via window events scoped to the frame.
    this.onSetLayers = this.onSetLayers.bind(this);
    window.addEventListener('response-time-chart:set-layers', this.onSetLayers);
    super.connect();
    window.dispatchEvent(
      new CustomEvent('response-time-chart:ready', {
        detail: { frame: this.frameId },
      })
    );
  }

  disconnect() {
    window.removeEventListener(
      'response-time-chart:set-layers',
      this.onSetLayers
    );
    super.disconnect();
  }

  get frameId() {
    return this.element.closest('turbo-frame')?.id ?? null;
  }

  onSetLayers(event) {
    if (event.detail?.frame !== this.frameId) return;
    this.visibleLayers = new Set(event.detail.layers);
    this.applyVisibility();
  }

  get compareIds() {
    return new Set(
      this.chartDataValue.responseTime.datasets.map((ds) => ds.compareId)
    );
  }

  initChart() {
    this.chart?.destroy();

    const data = this.combinedChartData();
    const theme = this.chartTheme();
    const tickStep = this.chartDataValue.responseTime.axis?.tickStep ?? 1;
    const ctx = this.canvasTarget.getContext('2d');
    const maxResponseTime = computeMaxResponseTime(data.datasets);
    const stepSize = computeStepSize(maxResponseTime);

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: data.labels,
        datasets: data.datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: this.xScaleOptions(theme.mutedColor, tickStep),
          y: {
            type: 'linear',
            position: 'left',
            title: {
              display: true,
              text: t('js.charts.response_time.response_time_seconds'),
              color: theme.mutedColor,
            },
            beginAtZero: true,
            grid: { color: theme.gridColor },
            ticks: {
              stepSize,
              color: theme.mutedColor,
              callback: (val) => formatSeconds(val),
            },
          },
          y1: {
            type: 'linear',
            position: 'right',
            title: {
              display: true,
              text: t('js.charts.response_time.alarm_count_per_hour'),
              color: theme.mutedColor,
            },
            beginAtZero: true,
            grid: { drawOnChartArea: false },
            ticks: {
              color: theme.mutedColor,
              callback: (val) => formatAlarmCount(val),
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: tooltipOptions((context) => {
            if (context.dataset.type === 'bar') {
              return t('js.charts.response_time.avg_alarms', {
                label: context.dataset.label,
                value: formatAlarmCount(context.raw),
              });
            }
            return `${context.dataset.label}: ${formatSeconds(context.raw)}`;
          }),
        },
      },
    });
  }

  combinedChartData() {
    return {
      labels: this.chartDataValue.responseTime.labels,
      datasets: [
        ...this.chartDataValue.alarmCount.datasets,
        ...this.chartDataValue.responseTime.datasets,
      ],
    };
  }

  xScaleOptions(mutedColor, tickStep) {
    return {
      grid: { display: false },
      ticks: {
        color: mutedColor,
        callback: function (_value, index) {
          if (index % tickStep !== 0) return '';
          return this.getLabelForValue(index).replace(/ \d{2}:\d{2}$/, '');
        },
      },
    };
  }

  applyVisibility() {
    if (!this.chart) return;

    const hasSelections = this.selectedItems.size > 0;

    this.chart.data.datasets.forEach((dataset, index) => {
      const layerMatch = this.visibleLayers.has(dataset.type);
      let compareMatch = true;

      if (dataset.type === 'line') {
        compareMatch =
          dataset.compareId === 'aggregate'
            ? !hasSelections
            : this.selectedItems.has(dataset.compareId);
      }

      this.chart.setDatasetVisibility(index, compareMatch && layerMatch);
    });

    this.chart.options.scales.y.display = this.visibleLayers.has('line');
    this.chart.options.scales.y1.display = this.visibleLayers.has('bar');

    this.chart.update();
  }

  paintChartColors() {
    this.chart.data.datasets.forEach((ds) => {
      if (!ds.compareId || ds.compareId === 'aggregate') return;
      const color = this.colorPool.get(ds.compareId);
      if (!color) return;
      ds.borderColor = color;
      ds.backgroundColor = color;
    });
  }
}

function tooltipOptions(labelCallback) {
  return {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    titleColor: '#fff',
    bodyColor: '#fff',
    cornerRadius: 6,
    padding: 10,
    filter: (item) => item.raw !== 0 && item.raw !== null,
    callbacks: { label: labelCallback },
  };
}

function computeMaxResponseTime(datasets) {
  let max = 0;
  for (const ds of datasets) {
    if (ds.type !== 'line') continue;
    for (const val of ds.data) {
      if (val != null && val > max) max = val;
    }
  }
  return max;
}

function computeStepSize(maxValue) {
  if (maxValue === 0) return 60;
  for (const step of TICK_STEPS) {
    if (maxValue / step <= 8) return step;
  }
  return TICK_STEPS[TICK_STEPS.length - 1];
}

function formatSeconds(seconds) {
  if (seconds == null || seconds === 0) {
    return t('js.charts.response_time.seconds', { seconds: 0 });
  }

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);

  const parts = [];
  if (hrs > 0) parts.push(t('js.charts.common.hours', { hours: hrs }));
  if (mins > 0) {
    parts.push(t('js.charts.common.minutes', { minutes: mins }));
  }
  if (secs > 0 || parts.length === 0) {
    parts.push(t('js.charts.response_time.seconds', { seconds: secs }));
  }
  return parts.join(' ');
}

function formatAlarmCount(count) {
  if (count == null || count === 0) return '0';
  return Number.isInteger(count) ? `${count}` : count.toFixed(2);
}
